import { Component, Input } from '@angular/core';
import { DriverCard, Orders, Trailers, AssignOrder } from '../../interfaces';
import { DataServiceService } from '../../services/data-service.service';
import { MessageService } from 'primeng/api';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-assign-to-new-order-dialog',
  templateUrl: './assign-to-new-order-dialog.component.html',
  styleUrl: './assign-to-new-order-dialog.component.scss'
})
export class AssignToNewOrderDialogComponent {

  constructor(
    private dataServiceService: DataServiceService,
    private messageService: MessageService
  ) { }

  @Input() driverCards: DriverCard[] = [];
  @Input() selectedDriverDispatch: number = -1;
  @Input() selectedOrder: Orders | null = null;
  @Input() trailers: Trailers[] = [];

  showTrailerDialog: boolean = false;

  // Trailer selection
  trailerType: 'existing' | 'external' | 'none' = 'existing';
  trailerTypeOptions = [
    { label: 'Existing Trailer', value: 'existing' },
    { label: 'Use another trailer', value: 'external' },
    { label: 'Assign Later', value: 'none' }
  ];
  trailerOptions: { label: string, value: number }[] = [];
  selectedTrailerId: number | null = null;
  externalTrailerNumber: string = '';

  // Scheduling
  pickupDate: Date | null = null;
  deliveryDate: Date | null = null;
  showPickupTime: boolean = false;
  showDeliveryTime: boolean = false;

  // Convenience method the parent can call
  open(order: Orders, selectedDriverId: number, trailers: Trailers[]) {
    this.selectedOrder = order;
    this.selectedDriverDispatch = selectedDriverId;
    this.trailers = trailers ?? [];
    this.trailerOptions = (this.trailers || [])
      .map(t => ({ label: t.trailer_number, value: t.trailer_id }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    this.trailerType = !order.trailer_id && !order.external_trailer_number ? 'existing' : 'none';
    this.selectedTrailerId = order.trailer_id ?? null;
    this.externalTrailerNumber = order.external_trailer_number ?? '';
    this.pickupDate = order.pickup_date ? this.convertUTCToCentral(order.pickup_date) : null;
    this.deliveryDate = order.delivery_date ? this.convertUTCToCentral(order.delivery_date) : null;
    this.showPickupTime = !!order.pickup_date;
    this.showDeliveryTime = !!order.delivery_date;
    this.showTrailerDialog = true;
  }

  skipTrailerAssignment() {
    const targetDriver = this.driverCards.find(d => d.driver.driver_id === this.selectedDriverDispatch);
    if (targetDriver) {
      this.assignOrderToDriver(targetDriver);
    }
    this.showTrailerDialog = false;
    this.selectedTrailerId = null;
  }

  assignTrailer() {
    if (!this.selectedOrder) return;

    if (this.trailerType === 'existing' && !this.selectedTrailerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a trailer type or select  Assign Later'
      });
      return;
    }

    // Validate by type
    if (this.trailerType === 'existing' && !this.selectedTrailerId) return;
    if (this.trailerType === 'external' && !this.externalTrailerNumber?.trim()) return;

    const trailerId = this.trailerType === 'existing' ? (this.selectedTrailerId ?? null) : null;
    const externalTrailerNumber = this.trailerType === 'external' ? (this.externalTrailerNumber || null) : null;

    const updates: Partial<Orders> = {
      trailer_id: trailerId ?? undefined,
      external_trailer_number: externalTrailerNumber ?? undefined,
      pickup_date: this.pickupDate ? this.convertCentralToUTC(this.pickupDate) as any : (this.selectedOrder.pickup_date ? this.selectedOrder.pickup_date : undefined),
      delivery_date: this.deliveryDate ? this.convertCentralToUTC(this.deliveryDate) as any : (this.selectedOrder.delivery_date ? this.selectedOrder.delivery_date : undefined)
    };

    this.dataServiceService.updateOrder(this.selectedOrder.order_id, updates).subscribe({
      next: () => {
        const targetDriver = this.driverCards.find(d => d.driver.driver_id === this.selectedDriverDispatch);
        if (targetDriver) {
          this.assignOrderToDriver(targetDriver);
        }
        this.showTrailerDialog = false;

        // Show success message
        this.messageService.add({
          severity: 'success',
          summary: 'Order Assigned',
          detail: `Order has been assigned to ${targetDriver?.driver.first_name} ${targetDriver?.driver.last_name}`,
          life: 3000
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update order before assignment'
        });
        console.error(err);
      }
    });
  }

  private assignOrderToDriver(targetDriver: DriverCard) {
    if (!this.selectedOrder) return;

    const orderData: AssignOrder = {
      order_id: this.selectedOrder.order_id,
      priority: String(targetDriver.dispatchOrders?.length + 1 || 1),
      scheduled_time: new Date().toISOString(),
      notes: this.selectedOrder.notes || ''
    };

    this.dataServiceService.assignOrderToDriverDispatch(
      targetDriver.driver.driver_id,
      targetDriver.dailyDispatch.id,
      orderData
    ).subscribe({
      next: () => {
        // Order successfully assigned, dialog will close via the parent method
      },
      error: (error) => {
        console.error('Failed to assign order:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Assignment Failed',
          detail: 'Failed to assign order to driver',
          life: 3000
        });
      }
    });
  }

  // Convert UTC date from database to Central Time Date object for calendar display
  private convertUTCToCentral(date: Date | string): Date {
    const utcDate = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralDate = utcDate.setZone('America/Chicago');
    return centralDate.toJSDate();
  }

  // Convert local date to Central Time, then to UTC for database storage
  private convertCentralToUTC(date: Date | string): string {
    const centralDate = DateTime.fromJSDate(new Date(date), { zone: 'America/Chicago' });
    return centralDate.setZone('utc').toISO() as string;
  }
}